import * as yup from 'yup';
import logger from '@gral/log';

import { motion, AnimatePresence } from 'framer-motion';
import { styled, Theme } from '@mui/material/styles';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import { Icon } from '@ts/storybook';
import { useState, useEffect } from 'react';

import { makeStyles } from '@mui/styles';
import { useAppDispatch } from '@redux/store';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import PreviewStepper from './PreviewStepper';
import { TMode, IConfigStepper } from '@ts/ui-models';
import { CEntity, IEntity } from '@ts/ui-models';
import { isEqual, pickBy, cloneDeep } from 'lodash';
import { patchEntity, postNewEntity } from '@services/ApiService';
import { useRouterContext } from '@utils/context/RouterContext';
import { Button, Grid, GridContainerFullHeight, Typography, TTextType } from '@ts/storybook';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 40,
  height: 40,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,1.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)',
  }),
}));

const StyleStepLabel = styled(StepLabel)(() => ({
  '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
    marginTop: '5px',
  },
}));

const useStyles = makeStyles(() => ({
  stepper: {
    paddingTop: '5px',
  },
  containerBody: {
    flex: 1,
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
    width: '100%',
    height: '100%',
    //paddingTop: '30px',
  },
  containerFields: {
    paddingTop: '40px',
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
}));

interface IStepState {
  id: number;
  completed: boolean;
  error: boolean;
}
export interface ISteps {
  label: TTextType;
  validation: () => yup.ObjectSchema<any>;
  formComponent: React.FC<any>;
  textInfo?: TTextType; //Paragraph info
  icon?: string; //If not icon we will use the number step
  isOptional?: boolean; //Skip validation
}

type ITSteps = ISteps & IStepState;

interface StepperProps {
  configStepper: IConfigStepper;
  mode: TMode;
  formData: CEntity<IEntity> | null;
  setFormData: (attr: any) => (e: any, v: any) => void;
  steps: ISteps[];
  onSubmit?: () => Promise<any>;
}

const log = logger(`STEPPER`);

const TStepper = (props: StepperProps) => {
  const { mode, formData: entity } = props;
  const { navigate, transferPropsRoute } = useRouterContext();
  const crudId = transferPropsRoute.params?.id;
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [steps, setSteps] = useState<ITSteps[]>([]);
  const [dataCompare, setDataCompare] = useState<any>({}); //To compare de data in each step
  const [entityLoaded, setEntityLoaded] = useState<any>(null); //To compare de data in submit

  //#region Handlers
  const handleBack = () => {
    setDirection(-1);
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleCancel = async () => {
    if (mode === 'edit') {
      log.info('Go to view mode');
      const navig = `/${entity?.tEntity}/${entity?.getID()}/view`;
      const titleNewRoute = `${entity?.getLabelEntity()} | ${entity?.toString()}`;

      navigate(`${navig}`, { state: { title: titleNewRoute } });
      return;
    } else navigate(-1);
  };

  const handleNext = async () => {
    if (mode === 'view') {
      log.info('Go to edit mode');
      const navig = `/${entity?.tEntity}/${entity?.getID()}/edit`;
      const titleNewRoute = `${entity?.getLabelEntity()} | ${entity?.toString()}`;

      navigate(`${navig}`, { state: { title: titleNewRoute } });
      return;
    }
    const validationFn = steps[activeStep].validation;

    try {
      await validationFn().validate(entity, { abortEarly: false });
      if (steps.length - 1 === activeStep) {
        log.info('Last step validation');
        const validationOk = await fullValidation();
        log.info(validationOk);
        if (validationOk) {
          const cEntity = new CEntity(validationOk);
          log.info('Go to view mode after create', cEntity);
          const navig = `/${entity?.tEntity}/${cEntity?.getID()}/view`;
          const titleNewRoute = `${entity?.getLabelEntity()} | ${cEntity?.toString()}`;

          navigate(`${navig}`, { state: { title: titleNewRoute } });
        }
        return;
      }
      setDirection(1);
      setSteps(prevSteps => {
        prevSteps[activeStep].completed = true;
        return prevSteps;
      });
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setDataCompare(cloneDeep(entity));
    } catch (e: yup.ValidationError | any) {
      validationError(e);
    }
  };

  const onClickStep = (step: ITSteps) => {
    if (step.completed) {
      setDirection(0);
      setActiveStep(step.id);
    }
  };
  //#endregion

  //#region Functions
  const getDifference = (newObj, oldObj = {}) => {
    return pickBy(newObj, (value, key) => !isEqual(value, oldObj[key]));
  };

  const getNextButtonLabel = () => {
    const difference = entity ? getDifference(entity, dataCompare) : {};

    if (steps[activeStep].isOptional) {
      return Object.keys(difference).length === 0 ? 'Skip' : 'Next';
    }
    return activeStep === steps.length - 1 ? (mode === 'view' ? 'Edit' : 'Submit') : 'Next';
  };

  const buildTSteps = (step: ISteps[]): ITSteps[] => {
    const previewStep = {
      id: props.steps.length,
      completed: false,
      error: false,
      label: 'Preview',
      icon: 'preview',
      validation: () => yup.object(),
      formComponent: props => <PreviewStepper {...props} />,
    };
    if (mode === 'view') return [previewStep];

    return [
      ...step.map((s, i) => {
        return {
          ...s,
          id: i,
          error: false,
          completed: false,
        };
      }),
      previewStep,
    ];
  };

  const variantAnimation = () => {
    return {
      enter: direction => ({
        x: direction === 0 ? 0 : direction > 0 ? 1000 : -1000,
        opacity: 0,
      }),
      center: {
        zIndex: 1,
        x: 0,
        opacity: 1,
        transition: { duration: 0.5 },
      },
      exit: direction => ({
        zIndex: 0,
        x: direction === 0 ? 0 : direction < 0 ? 1000 : -1000,
        opacity: 0,
        transition: { duration: 0.5 },
      }),
    };
  };

  const colorlibStepIcon = (props: StepIconProps, iconLabel?: string) => {
    const { active, completed, className, icon } = props;

    return (
      <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
        {iconLabel ? (
          <Icon size={20} icon={iconLabel} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: '600' }} text={icon?.toString()} />
        )}
      </ColorlibStepIconRoot>
    );
  };

  //#region Validation
  const validationSuccess = () => {};
  const validationError = (e: yup.ValidationError | any) => {
    log.info('Validation error', e);
    let message: string | string[] = '';
    const missingFieldLabels: string[] = [];
    const requiredFields: string[] = [];
    if (e.inner) {
      e.inner.forEach(inError => {
        if (inError.type === 'validation') {
          message = ['Required field missing', ': ', inError.message];
        } else if (inError.type === 'matches') {
          requiredFields.push(inError.path);
          message = [inError.params.label, ' - ', inError.message];
        } else if (inError.type !== 'required') {
          message = inError.message;
        } else {
          missingFieldLabels.push(inError.params.label || inError.params.path);
          requiredFields.push(inError.path);
          message = ['Required field missing', ': ', missingFieldLabels.join(', ')];
        }
      });
      dispatch(globalActions.notification(message));
      setRequiredFields(requiredFields);
    }
  };

  const fullValidation = async () => {
    const combinedValidation = steps
      .map(s => s.validation)
      .reduce((result, validation) => {
        return result.concat(validation());
      }, yup.object());

    await combinedValidation.validate(entity, { abortEarly: false });

    if (props.onSubmit) {
      const response = await props.onSubmit();
      if (response.error) throw new Error();
    } else {
      //Default on submit entity post and patch
      if (!crudId && entity) {
        log.info('Post entity');
        return await postNewEntity(dispatch, props.configStepper, entity);
      } else {
        const difference = getDifference(entity, entityLoaded);
        log.info('Patch entity');
        return await patchEntity(dispatch, props.configStepper, crudId, difference);
      }
    }
  };
  //#endregion
  //#endregion

  //#region Render
  const renderFooter = () => {
    const nextButtonLabel = getNextButtonLabel();

    return (
      <Grid container spacing={2} p={2} justifyContent="center">
        <Grid size={{ xs: 5 }}>
          <Button
            fullWidth
            onClick={activeStep === 0 ? handleCancel : handleBack}
            variant="outlined"
            label={activeStep === 0 && mode === 'edit' ? 'Cancel' : 'Back'}
          />
        </Grid>

        <Grid size={{ xs: 5 }}>
          <Button
            fullWidth
            onClick={handleNext}
            variant={nextButtonLabel === 'Skip' ? 'outlined' : 'contained'}
            label={nextButtonLabel}
          />
        </Grid>
      </Grid>
    );
  };
  //#endregion

  //BUILD Steps
  useEffect(() => {
    setActiveStep(0);
    setSteps(buildTSteps(props.steps));
  }, [mode]);

  //CheckLastStep
  useEffect(() => {
    if (activeStep === steps.length - 1) {
      setSteps(prevSteps => {
        prevSteps[activeStep].completed = true;
        return prevSteps;
      });
    }
  }, [activeStep]);

  //Load entity to compare on submit
  useEffect(() => {
    if (entityLoaded === null && entity) {
      setEntityLoaded(cloneDeep(entity));
    }
  }, [entity]);

  const CurrentForm = steps[activeStep]?.formComponent;
  const classStyle = useStyles();

  if (!CurrentForm || entity === null) return;

  return (
    <GridContainerFullHeight>
      <Stepper className={classStyle.stepper} alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
        {steps.map(step => (
          <Step key={`step-${step.id}`} completed={step.completed} onClick={() => onClickStep(step)} sx={{ cursor: 'pointer' }}>
            <StyleStepLabel error={step.error} StepIconComponent={props => colorlibStepIcon(props, step.icon)}>
              {
                <Typography
                  variant="body2"
                  justify="center"
                  sx={{ fontWeight: steps[activeStep].id === step.id ? '600' : '400' }}
                  text={step.label}
                />
              }
            </StyleStepLabel>
          </Step>
        ))}
      </Stepper>
      <div className={classStyle.containerBody}>
        <AnimatePresence custom={direction}>
          <motion.div
            key={`motion-${activeStep}`}
            custom={direction}
            variants={variantAnimation()}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            className={classStyle.containerFields}
          >
            <Grid container justifyContent="center">
              {steps[activeStep].textInfo && (
                <Grid size={{ xs: 12, md: 10 }} mt={2} key={`textInfo-${activeStep}`}>
                  <Typography variant="body1" justify="justify" text={steps[activeStep].textInfo} />
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 10 }} mt={4} key={`form-${activeStep}`}>
                <CurrentForm
                  mode={mode}
                  steps={props.steps}
                  formData={entity}
                  setFormData={props.setFormData}
                  requiredFields={requiredFields}
                />
              </Grid>
            </Grid>
          </motion.div>
        </AnimatePresence>
      </div>
      {renderFooter()}
    </GridContainerFullHeight>
  );
};

export default TStepper;
